/**
 * Chunked file encryption/decryption module
 * Supports streaming large files (images/videos) in ~512KB chunks
 */

export interface EncryptedChunk {
  index: number;        // Block index
  total: number;        // Total number of blocks
  data: string;         // Encrypted base64 data
  iv: string;           // IV (base64)
}

export interface FileManifest {
  id: string;           // Unique file ID
  filename: string;     // Encrypted filename
  mimeType: string;     // File MIME type
  totalChunks: number;  // Total number of chunks
  totalSize: number;    // Total file size
  thumbnail?: string;   // Encrypted thumbnail (optional, for videos)
}

export interface ChunkInfo {
  index: number;
  total: number;
  fileId: string;
}

// Default chunk size: 512KB
const DEFAULT_CHUNK_SIZE = 512 * 1024;

/**
 * Read file in chunks using File.slice() for memory-efficient streaming
 * Uses FileReader to read each chunk as ArrayBuffer
 */
export async function* readFileInChunks(
  file: File,
  chunkSize: number = DEFAULT_CHUNK_SIZE
): AsyncGenerator<{ chunk: ArrayBuffer; index: number; total: number }> {
  const total = Math.ceil(file.size / chunkSize);

  for (let index = 0; index < total; index++) {
    const start = index * chunkSize;
    const end = Math.min(start + chunkSize, file.size);

    // Use File.slice() to get a Blob without copying memory
    const blob = file.slice(start, end);

    // Read the chunk as ArrayBuffer
    const chunk = await new Promise<ArrayBuffer>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = () => reject(new Error('Failed to read file chunk'));
      reader.readAsArrayBuffer(blob);
    });

    yield { chunk, index, total };
  }
}

/**
 * Convert ArrayBuffer to base64 string with chunking to avoid stack overflow
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 65536; // Process in 64KB chunks to avoid stack overflow
  let result = '';

  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    result += String.fromCharCode(...chunk);
  }

  return btoa(result);
}

/**
 * Convert base64 string to ArrayBuffer
 */
function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Encrypt a single chunk with AES-256-GCM
 * Each chunk uses a unique IV
 */
async function encryptChunk(
  cryptoKey: CryptoKey,
  chunk: ArrayBuffer
): Promise<{ data: string; iv: string }> {
  const iv = crypto.getRandomValues(new Uint8Array(12));

  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    cryptoKey,
    chunk
  );

  // Combine IV + ciphertext
  const combined = new Uint8Array(iv.length + ciphertext.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(ciphertext), iv.length);

  return {
    data: arrayBufferToBase64(combined.buffer),
    iv: arrayBufferToBase64(iv.buffer),
  };
}

/**
 * Encrypt file in chunks and yield encrypted chunks
 * Memory-efficient: only holds current chunk in memory
 */
export async function* encryptFileInChunks(
  cryptoKey: CryptoKey,
  file: File,
  chunkSize: number = DEFAULT_CHUNK_SIZE
): AsyncGenerator<EncryptedChunk> {
  let index = 0;
  const total = Math.ceil(file.size / chunkSize);

  for await (const { chunk } of readFileInChunks(file, chunkSize)) {
    const encrypted = await encryptChunk(cryptoKey, chunk);

    yield {
      index,
      total,
      data: encrypted.data,
      iv: encrypted.iv,
    };

    index++;
  }
}

/**
 * Decrypt a single encrypted chunk
 */
export async function decryptChunk(
  cryptoKey: CryptoKey,
  encryptedChunk: EncryptedChunk
): Promise<ArrayBuffer> {
  const combined = base64ToArrayBuffer(encryptedChunk.data);

  const iv = combined.slice(0, 12);
  const ciphertext = combined.slice(12);

  return crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: new Uint8Array(iv) },
    cryptoKey,
    ciphertext
  );
}

/**
 * Class to assemble file chunks in memory
 * Handles out-of-order arrival and final assembly
 */
export class ChunkedFileAssembler {
  private chunks: Map<number, ArrayBuffer> = new Map();
  private receivedChunks = 0;

  constructor(
    public readonly manifest: FileManifest,
    private onProgress?: (received: number, total: number) => void
  ) {}

  /**
   * Add a decrypted chunk to the assembler
   */
  addChunk(index: number, data: ArrayBuffer): void {
    if (this.chunks.has(index)) {
      return; // Already have this chunk
    }

    this.chunks.set(index, data);
    this.receivedChunks++;

    if (this.onProgress) {
      this.onProgress(this.receivedChunks, this.manifest.totalChunks);
    }
  }

  /**
   * Check if all chunks have been received
   */
  isComplete(): boolean {
    return this.receivedChunks === this.manifest.totalChunks;
  }

  /**
   * Get the number of received chunks
   */
  getReceivedCount(): number {
    return this.receivedChunks;
  }

  /**
   * Assemble all chunks into a Blob
   * Chunks are sorted by index before assembly
   */
  assemble(): Blob {
    if (!this.isComplete()) {
      throw new Error(`Cannot assemble: only ${this.receivedChunks}/${this.manifest.totalChunks} chunks received`);
    }

    // Sort chunks by index and combine
    const sortedChunks: ArrayBuffer[] = [];
    for (let i = 0; i < this.manifest.totalChunks; i++) {
      const chunk = this.chunks.get(i);
      if (!chunk) {
        throw new Error(`Missing chunk at index ${i}`);
      }
      sortedChunks.push(chunk);
    }

    return new Blob(sortedChunks, { type: this.manifest.mimeType });
  }

  /**
   * Create object URL for the assembled file
   * Caller is responsible for revoking the URL when done
   */
  createObjectURL(): string {
    const blob = this.assemble();
    return URL.createObjectURL(blob);
  }

  /**
   * Clean up stored chunks to free memory
   */
  cleanup(): void {
    this.chunks.clear();
    this.receivedChunks = 0;
  }
}

/**
 * Generate a unique file ID
 */
export function generateFileId(): string {
  return crypto.randomUUID();
}

/**
 * Generate video thumbnail from first frame
 * Returns base64 encoded JPEG thumbnail
 */
export async function generateVideoThumbnail(file: File): Promise<string | null> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);

    video.addEventListener('loadeddata', () => {
      // Seek to 0.5 seconds or 1% of duration to get a good frame
      video.currentTime = Math.min(0.5, video.duration * 0.01);
    });

    video.addEventListener('seeked', () => {
      try {
        const canvas = document.createElement('canvas');
        // Limit thumbnail size
        const maxDimension = 320;
        let width = video.videoWidth;
        let height = video.videoHeight;

        if (width > maxDimension || height > maxDimension) {
          const ratio = Math.min(maxDimension / width, maxDimension / height);
          width = Math.floor(width * ratio);
          height = Math.floor(height * ratio);
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          URL.revokeObjectURL(url);
          resolve(null);
          return;
        }

        ctx.drawImage(video, 0, 0, width, height);
        const thumbnail = canvas.toDataURL('image/jpeg', 0.7);
        URL.revokeObjectURL(url);

        // Remove data URL prefix (data:image/jpeg;base64,)
        const base64Data = thumbnail.split(',')[1];
        resolve(base64Data);
      } catch (e) {
        URL.revokeObjectURL(url);
        resolve(null);
      }
    });

    video.addEventListener('error', () => {
      URL.revokeObjectURL(url);
      resolve(null);
    });

    video.src = url;
    video.load();
  });
}
